import { useState, useCallback, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useRememberFilters } from "@/hooks/useRememberFilters";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ExpandingSearch } from "@/components/ui/expanding-search";
import { SearchChip } from "@/components/ui/search-chip";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Gauge, Trash2, RefreshCw, Upload, Wrench, MapPin, AlertTriangle } from "lucide-react";
import { ChipFilter, ChipFilterOption } from "@/components/ui/chip-filter";
import { AddFilterMenu, FilterCategory } from "@/components/ui/add-filter-menu";
import { toast } from "sonner";
import { Tractor, TractorCapability, TractorBase, Location } from "@/types/tms";
import { useCRUDMutations } from "@/hooks/useCRUDMutations";
import { useSamsaraVehicles } from "@/hooks/useSamsaraVehicles";
import { TractorDeleteButton } from "@/components/tractors/TractorDeleteButton";
import { useTractorDeletion } from "@/hooks/useTractorDeletion";
import { Checkbox } from "@/components/ui/checkbox";
import { BulkActionBar } from "@/components/ui/bulk-action-bar";
import { useBulkSelection } from "@/hooks/useBulkSelection";
import { SortableHeader } from "@/components/ui/sortable-header";
import { useTableSort } from "@/hooks/useTableSort";
import { BulkTractorAdd } from "@/components/admin/BulkTractorAdd";
import { TractorDetailSheet } from "@/components/tractors/TractorDetailSheet";
import { useUrlFilterState, useUrlArrayFilterState } from "@/hooks/useUrlFilterState";
import { ResponsiveTableShell } from "@/components/ui/responsive-table-shell";
import { TablePagination } from "@/components/ui/table-pagination";
import { useTablePagination } from "@/hooks/useTablePagination";
import { useTruckEligibilityMap } from "@/hooks/useTruckEligibility";
import { eligibleModesDisplay } from "@/lib/truck-eligibility";
import { TractorCard } from "@/components/tractors/TractorCard";


const BASE_TRUCKS: TractorBase[] = ["Peterbilt", "Volvo", "International", "Daimler"];

type TractorFormData = {
  name: string;
  capabilities: TractorCapability[];
  base_truck: TractorBase | "";
  home_base_id: string;
  samsara_id: string;
};

const transformTractorData = (data: TractorFormData) => ({
  ...data,
  base_truck: data.base_truck || null,
  home_base_id: data.home_base_id || null,
  samsara_id: data.samsara_id || null,
});

export default function Tractors() {
  const queryClient = useQueryClient();
  useRememberFilters();
  const [searchQuery, setSearchQuery] = useUrlFilterState<string>("q", "");
  const [baseTruckFilter, setBaseTruckFilter] = useUrlArrayFilterState("baseTruck");
  const [homeBaseFilter, setHomeBaseFilter] = useUrlArrayFilterState("homeBase");
  const [serviceStatusFilter, setServiceStatusFilter] = useUrlArrayFilterState("serviceStatus");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [editingTractor, setEditingTractor] = useState<Tractor | null>(null);
  const [selectedTractor, setSelectedTractor] = useState<(Tractor & { home_base?: Location }) | null>(null);
  const [formData, setFormData] = useState<TractorFormData>({
    name: "", capabilities: [], base_truck: "", home_base_id: "", samsara_id: "",
  });

  const handleSyncSamsara = useCallback(async () => {
    setIsSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke("sync-samsara-ids");
      if (error) throw error;
      const result = data as { matched: number; unmatched: string[]; total: number; samsaraVehicleCount: number };
      toast.success(`Matched ${result.matched} of ${result.total} tractors (${result.samsaraVehicleCount} Samsara vehicles found)`);
      if (result.unmatched.length > 0) {
        toast.info(`Unmatched: ${result.unmatched.join(", ")}`, { duration: 10000 });
      }
      queryClient.invalidateQueries({ queryKey: ["tractors"] });
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Sync failed");
    } finally {
      setIsSyncing(false);
    }
  }, [queryClient]);

  const { data: samsaraVehicles } = useSamsaraVehicles();

  const odometerMap = new Map<string, number>();
  samsaraVehicles?.forEach((v) => {
    if (v.odometerMeters != null) {
      odometerMap.set(v.name.trim().toLowerCase(), Math.round(v.odometerMeters / 1609.344));
    }
  });

  const { data: tractors, isLoading } = useQuery({
    queryKey: ["tractors"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tractors")
        .select(`*, home_base:locations(*)`)
        .order("name");
      if (error) throw error;
      return data as (Tractor & { home_base: Location })[];
    },
  });

  const { data: serviceStatusRows } = useQuery({
    queryKey: ["tractor-service-status"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tractor_service_status" as never)
        .select("tractor_id, open_service_count, status");
      if (error) throw error;
      return (data ?? []) as {
        tractor_id: string;
        open_service_count: number;
        status: string | null;
      }[];
    },
  });
  const serviceStatusMap = new Map<string, { count: number; oos: boolean }>();
  // Latest eligibility event per tractor (empty map when the flag is off).
  const { eligibilityByTractor, gateState: eligibilityGateState } = useTruckEligibilityMap();
  // Base table has 8 columns; the eligibility column adds one when enabled.
  const tableColSpan = 9;
  serviceStatusRows?.forEach((r) => {
    const oos =
      r.status === "oos_current" ||
      r.status === "oos_past_due" ||
      r.status === "oos_indefinite";
    serviceStatusMap.set(r.tractor_id, { count: r.open_service_count, oos });
  });

  const { data: locations } = useQuery({
    queryKey: ["locations"],
    queryFn: async () => {
      const { data } = await supabase.from("locations").select("*").eq("archived", false).order("short_code");
      return data as Location[];
    },
  });

  const filteredTractors = tractors?.filter((t) => {
    const matchesSearch = t.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesBaseTruck = baseTruckFilter.includes("all") || baseTruckFilter.includes(t.base_truck ?? "");
    const matchesHomeBase = homeBaseFilter.includes("all") || homeBaseFilter.includes(t.home_base_id ?? "");
    const oos = serviceStatusMap.get(t.id)?.oos ?? false;
    const matchesServiceStatus =
      serviceStatusFilter.includes("all") ||
      (serviceStatusFilter.includes("oos") && oos) ||
      (serviceStatusFilter.includes("in_service") && !oos);
    return matchesSearch && matchesBaseTruck && matchesHomeBase && matchesServiceStatus;
  });
  const hasActiveFilter =
    !!searchQuery.trim() ||
    !baseTruckFilter.includes("all") ||
    !homeBaseFilter.includes("all") ||
    !serviceStatusFilter.includes("all");

  type TractorWithBase = Tractor & { home_base: Location };
  const TRACTOR_ACCESSORS = {
    name: (t: TractorWithBase) => t.name,
    capability: (t: TractorWithBase) => t.capabilities?.[0],
    base_truck: (t: TractorWithBase) => t.base_truck,
    home_base: (t: TractorWithBase) => t.home_base?.short_code,
  };
  const { sorted: sortedTractors, sort, toggle: toggleSort } = useTableSort(filteredTractors as TractorWithBase[] | undefined, TRACTOR_ACCESSORS);

  const { selectedIds, toggle, toggleAll, clear, allSelected, someSelected, count } = useBulkSelection(sortedTractors);


  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    totalPages,
    totalCount,
    pageRows: pagedTractors,
  } = useTablePagination(sortedTractors as TractorWithBase[] | undefined);

  const resetForm = () => {
    setFormData({ name: "", capabilities: [], base_truck: "", home_base_id: "", samsara_id: "" });
    setEditingTractor(null);
    setIsDialogOpen(false);
  };

  const createMutation = useMutation({
    mutationFn: async (data: TractorFormData) => {
      const { error } = await supabase.from("tractors").insert(transformTractorData(data));
      if (error) throw error;
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ["tractors"] });
      toast.success("Tractor created successfully");
      resetForm();
      try {
        await supabase.functions.invoke("sync-samsara-ids");
        queryClient.invalidateQueries({ queryKey: ["tractors"] });
      } catch (e) {
        // Silent fail
      }
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const { updateMutation } = useCRUDMutations<TractorFormData>({
    tableName: "tractors",
    queryKey: "tractors",
    entityLabel: "Tractor",
    transformUpdate: transformTractorData,
    onUpdateSuccess: resetForm,
  });

  const { deleteTractor: deleteMutation, bulkDeleteTractors } = useTractorDeletion();

  const bulkDeleteMutation = {
    isPending: bulkDeleteTractors.isPending,
    mutate: (ids: string[]) =>
      bulkDeleteTractors.mutate(ids, { onSuccess: () => clear() }),
  };

  const handleEdit = (tractor: Tractor) => {
    setEditingTractor(tractor);
    setFormData({
      name: tractor.name,
      capabilities: tractor.capabilities || [],
      base_truck: tractor.base_truck || "",
      home_base_id: tractor.home_base_id || "",
      samsara_id: tractor.samsara_id || "",
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingTractor) {
      updateMutation.mutate({ id: editingTractor.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  return (
    <AppLayout
      title="Tractors"
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleSyncSamsara} disabled={isSyncing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isSyncing ? "animate-spin" : ""}`} />
            {isSyncing ? "Syncing..." : "Sync Samsara IDs"}
          </Button>
          <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline"><Upload className="h-4 w-4 mr-2" /> Import Tractors</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Import Tractors</DialogTitle>
              </DialogHeader>
              <BulkTractorAdd variant="dialog" onSuccess={() => setIsImportDialogOpen(false)} />
            </DialogContent>
          </Dialog>
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button><Plus className="h-4 w-4 mr-2" /> Add Tractor</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingTractor ? "Edit Tractor" : "Add Tractor"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Tractor Name *</Label>
                  <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., Truck-001" required />
                </div>
                <div>
                  <Label>Base Truck</Label>
                  <Select value={formData.base_truck} onValueChange={(value) => setFormData({ ...formData, base_truck: value as TractorBase })}>
                    <SelectTrigger><SelectValue placeholder="Select manufacturer" /></SelectTrigger>
                    <SelectContent>
                      {BASE_TRUCKS.map((truck) => (<SelectItem key={truck} value={truck}>{truck}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Home Base</Label>
                  <Select value={formData.home_base_id} onValueChange={(value) => setFormData({ ...formData, home_base_id: value })}>
                    <SelectTrigger><SelectValue placeholder="Select location" /></SelectTrigger>
                    <SelectContent>
                      {locations?.map((loc) => (<SelectItem key={loc.id} value={loc.id}>{loc.short_code}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="samsara_id">Samsara ID</Label>
                  <Input id="samsara_id" value={formData.samsara_id} readOnly disabled className="bg-muted cursor-not-allowed" placeholder="Auto-synced" />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                  <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>{editingTractor ? "Update" : "Create"}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      }
    >
      <div className="space-y-4 animate-fade-in">
        <div className="flex items-center gap-3">
          <SearchChip query={searchQuery} onClear={() => setSearchQuery("")} />
          <TractorFilters
            baseTruckFilter={baseTruckFilter}
            onBaseTruckFilterChange={setBaseTruckFilter}
            homeBaseFilter={homeBaseFilter}
            onHomeBaseFilterChange={setHomeBaseFilter}
            serviceStatusFilter={serviceStatusFilter}
            onServiceStatusFilterChange={setServiceStatusFilter}
            locations={locations}
          />
          <div className="ml-auto flex items-center gap-3">
            <span className="text-sm text-muted-foreground whitespace-nowrap">Showing {filteredTractors?.length ?? 0} tractor{(filteredTractors?.length ?? 0) === 1 ? "" : "s"}</span>
            <ExpandingSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search tractors..." />
          </div>
        </div>
        <div>
          <BulkActionBar count={count} onClear={clear} entityLabel="tractor">
            <Button
              variant="ghost"
              size="sm"
              className="text-primary-foreground hover:bg-primary-foreground/20 hover:text-primary-foreground"
              onClick={() => bulkDeleteMutation.mutate(Array.from(selectedIds))}
              disabled={bulkDeleteMutation.isPending}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete
            </Button>
          </BulkActionBar>
          <div className={`rounded-lg border bg-card ${count > 0 ? "rounded-t-none border-t-0" : ""}`}>
          <ResponsiveTableShell
            cards={
              <div className="flex flex-col gap-3 p-3">
                {isLoading ? (
                  <div className="text-center py-8 text-muted-foreground">Loading...</div>
                ) : pagedTractors.length ? (
                  pagedTractors.map((tractor) => {
                    const svc = serviceStatusMap.get(tractor.id);
                    // One computation per card: text and tone must agree.
                    const el = eligibleModesDisplay(
                      eligibilityByTractor.get(tractor.id),
                      eligibilityGateState,
                    );
                    return (
                      <TractorCard
                        key={tractor.id}
                        tractor={tractor}
                        isChecked={selectedIds.has(tractor.id)}
                        onToggle={toggle}
                        onClick={setSelectedTractor}
                        onEdit={handleEdit}
                        onDelete={(id) => deleteMutation.mutate(id)}
                        odometerMiles={odometerMap.get(tractor.name.trim().toLowerCase())}
                        isOos={svc?.oos ?? false}
                        openServiceCount={svc?.count}
                        eligibleModes={el?.text}
                        eligibleModesBlocked={el?.showAsBlocked ?? false}
                      />
                    );
                  })
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    {hasActiveFilter ? "No tractors match your filter criteria" : "No tractors found"}
                  </div>
                )}
              </div>
            }
            table={
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={toggleAll}
                    aria-label="Select all"
                    {...(someSelected ? { "data-state": "indeterminate" } : {})}
                  />
                </TableHead>
                <TableHead><SortableHeader label="Tractor" sortKey="name" currentKey={sort.key} currentDirection={sort.direction} onToggle={toggleSort} /></TableHead>
                
                <TableHead><SortableHeader label="Base Truck" sortKey="base_truck" currentKey={sort.key} currentDirection={sort.direction} onToggle={toggleSort} /></TableHead>
                <TableHead><SortableHeader label="Home Base" sortKey="home_base" currentKey={sort.key} currentDirection={sort.direction} onToggle={toggleSort} /></TableHead>
                <TableHead>VIN</TableHead>
                <TableHead>Odometer</TableHead>
                <TableHead>Eligible modes</TableHead>
                <TableHead>Service</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={tableColSpan} className="text-center py-8">Loading...</TableCell></TableRow>
              ) : pagedTractors.length ? (
                pagedTractors.map((tractor) => {
                  const odoMiles = odometerMap.get(tractor.name.trim().toLowerCase());
                  const svc = serviceStatusMap.get(tractor.id);
                  const isOos = svc?.oos ?? false;
                  return (
                   <TableRow key={tractor.id} selected={selectedTractor?.id === tractor.id} onClick={(e) => {
                     if ((e.target as HTMLElement).closest('button, input, a, [role="checkbox"]')) return;
                     setSelectedTractor(tractor);
                   }}>
                     <TableCell className="w-[40px]">
                       <Checkbox
                         checked={selectedIds.has(tractor.id)}
                         onCheckedChange={() => toggle(tractor.id)}
                       />
                     </TableCell>
                     <TableCell>
                       <span className="flex items-center gap-1.5">
                         {isOos && (
                           <AlertTriangle
                             className="h-4 w-4 shrink-0 fill-destructive text-background"
                             aria-label="Out of service"
                           />

                         )}
                         <span className="font-medium">{tractor.name}</span>
                       </span>
                     </TableCell>

                    <TableCell>{tractor.base_truck || "—"}</TableCell>
                    <TableCell>{tractor.home_base?.short_code || "—"}</TableCell>
                    <TableCell><span className="font-mono text-xs">{tractor.vin || "—"}</span></TableCell>
                    <TableCell>
                      {odoMiles != null ? (
                        <div className="flex items-center gap-1.5 text-sm">
                          <Gauge className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="font-mono">{odoMiles.toLocaleString()}</span>
                          <span className="text-xs text-muted-foreground">mi</span>
                        </div>
                      ) : "—"}
                    </TableCell>
                    {(() => {
                      const el = eligibleModesDisplay(
                        eligibilityByTractor.get(tractor.id),
                        eligibilityGateState,
                      );
                      return (
                        <TableCell>
                          <span
                            data-testid={`eligible-modes-${tractor.id}`}
                            className={
                              el.showAsBlocked
                                ? "text-xs font-medium text-destructive"
                                : "text-xs text-muted-foreground"
                            }
                          >
                            {el.text}
                          </span>
                        </TableCell>
                      );
                    })()}
                    <TableCell>
                      {isOos ? (
                        <span className="inline-flex items-center gap-1 rounded-md bg-destructive/15 px-2 py-0.5 text-xs font-medium text-destructive">
                          <AlertTriangle className="h-3 w-3" />
                          Out of Service
                          {svc && svc.count > 1 ? <span className="ml-0.5 opacity-70">({svc.count})</span> : null}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">In Service</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(tractor)}><Pencil className="h-4 w-4" /></Button>
                        <TractorDeleteButton
                          tractorId={tractor.id}
                          tractorName={tractor.name}
                          onConfirm={() => deleteMutation.mutate(tractor.id)}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={tableColSpan} className="text-center py-8 text-muted-foreground">
                    {hasActiveFilter ? "No tractors match your filter criteria" : "No tractors found"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
            }
          />
          <TablePagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            totalPages={totalPages}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </div>
        </div>
      </div>

      <TractorDetailSheet
        tractor={selectedTractor}
        open={!!selectedTractor}
        onOpenChange={(open) => { if (!open) setSelectedTractor(null); }}
        onEdit={handleEdit}
        odometerMiles={selectedTractor ? odometerMap.get(selectedTractor.name.trim().toLowerCase()) : undefined}
      />
    </AppLayout>
  );
}

type TractorFilterKey = "baseTruck" | "homeBase" | "serviceStatus";

const BASE_TRUCK_OPTIONS: ChipFilterOption[] = BASE_TRUCKS.map((b) => ({ value: b, label: b }));

const SERVICE_STATUS_OPTIONS: ChipFilterOption[] = [
  { value: "in_service", label: "In Service" },
  { value: "oos", label: "Out of Service" },
];

function TractorFilters({
  baseTruckFilter,
  onBaseTruckFilterChange,
  homeBaseFilter,
  onHomeBaseFilterChange,
  serviceStatusFilter,
  onServiceStatusFilterChange,
  locations,
}: {
  baseTruckFilter: string[];
  onBaseTruckFilterChange: (v: string[]) => void;
  homeBaseFilter: string[];
  onHomeBaseFilterChange: (v: string[]) => void;
  serviceStatusFilter: string[];
  onServiceStatusFilterChange: (v: string[]) => void;
  locations: Location[] | undefined;
}) {
  const [activeFilters, setActiveFilters] = useState<TractorFilterKey[]>(() => {
    const initial: TractorFilterKey[] = [];
    if (!baseTruckFilter.includes("all")) initial.push("baseTruck");
    if (!homeBaseFilter.includes("all")) initial.push("homeBase");
    if (!serviceStatusFilter.includes("all")) initial.push("serviceStatus");
    return initial;
  });
  const [openFilterKey, setOpenFilterKey] = useState<TractorFilterKey | null>(null);
  const ignoreAutoCloseRef = useRef<TractorFilterKey | null>(null);

  const addFilter = useCallback((key: string) => {
    const fk = key as TractorFilterKey;
    if (activeFilters.includes(fk)) return;
    setActiveFilters((prev) => [...prev, fk]);
    window.setTimeout(() => {
      ignoreAutoCloseRef.current = fk;
      setOpenFilterKey(fk);
      window.setTimeout(() => {
        if (ignoreAutoCloseRef.current === fk) ignoreAutoCloseRef.current = null;
      }, 250);
    }, 50);
  }, [activeFilters]);

  const removeFilter = useCallback((key: TractorFilterKey) => {
    setActiveFilters((prev) => prev.filter((k) => k !== key));
    setOpenFilterKey((prev) => (prev === key ? null : prev));
    if (ignoreAutoCloseRef.current === key) ignoreAutoCloseRef.current = null;
    if (key === "baseTruck") onBaseTruckFilterChange(["all"]);
    if (key === "homeBase") onHomeBaseFilterChange(["all"]);
    if (key === "serviceStatus") onServiceStatusFilterChange(["all"]);
  }, [onBaseTruckFilterChange, onHomeBaseFilterChange, onServiceStatusFilterChange]);

  const getFilterValue = (key: TractorFilterKey): string[] => {
    if (key === "baseTruck") return baseTruckFilter;
    if (key === "homeBase") return homeBaseFilter;
    if (key === "serviceStatus") return serviceStatusFilter;
    return ["all"];
  };

  const hasAnyChipFilter = activeFilters.length > 0;

  const resetAllFilters = () => {
    setActiveFilters([]);
    setOpenFilterKey(null);
    ignoreAutoCloseRef.current = null;
    onBaseTruckFilterChange(["all"]);
    onHomeBaseFilterChange(["all"]);
    onServiceStatusFilterChange(["all"]);
  };

  const locationOptions: ChipFilterOption[] = (locations ?? []).map((loc) => ({
    value: loc.id,
    label: loc.short_code,
  }));

  const allCategories: (FilterCategory & { key: TractorFilterKey })[] = [
    { key: "baseTruck", label: "Base Truck", icon: <Wrench className="h-3.5 w-3.5" /> },
    { key: "homeBase", label: "Home Base", icon: <MapPin className="h-3.5 w-3.5" /> },
    { key: "serviceStatus", label: "Service Status", icon: <AlertTriangle className="h-3.5 w-3.5" /> },
  ];

  const availableCategories = allCategories
    .filter((c) => !activeFilters.includes(c.key))
    .map(({ key, label, icon }) => ({ key, label, icon }));

  const optionsMap: Record<TractorFilterKey, { label: string; options: ChipFilterOption[]; value: string[]; onChange: (v: string | string[]) => void }> = {
    baseTruck: { label: "Base Truck", options: BASE_TRUCK_OPTIONS, value: baseTruckFilter, onChange: (v) => onBaseTruckFilterChange(Array.isArray(v) ? v : [v]) },
    homeBase: { label: "Home Base", options: locationOptions, value: homeBaseFilter, onChange: (v) => onHomeBaseFilterChange(Array.isArray(v) ? v : [v]) },
    serviceStatus: { label: "Service Status", options: SERVICE_STATUS_OPTIONS, value: serviceStatusFilter, onChange: (v) => onServiceStatusFilterChange(Array.isArray(v) ? v : [v]) },
  };

  const renderChip = (key: TractorFilterKey) => {
    const cfg = optionsMap[key];
    const categoryIcon = allCategories.find((c) => c.key === key)?.icon;
    return (
      <ChipFilter
        key={key}
        label={cfg.label}
        icon={categoryIcon}
        options={cfg.options}
        value={cfg.value}
        onChange={cfg.onChange}
        onRemove={() => removeFilter(key)}
        multiSelect
        open={openFilterKey === key}
        onOpenChange={(isOpen) => {
          if (!isOpen && ignoreAutoCloseRef.current === key) {
            ignoreAutoCloseRef.current = null;
            return;
          }
          if (!isOpen) {
            if (getFilterValue(key).includes("all")) {
              setActiveFilters((prev) => prev.filter((k) => k !== key));
            }
            setOpenFilterKey((prev) => (prev === key ? null : prev));
          } else {
            setOpenFilterKey(key);
          }
        }}
      />
    );
  };

  return (
    <div className="flex items-center gap-1 flex-wrap">
      {activeFilters.map((key) => renderChip(key))}
      <AddFilterMenu
        categories={availableCategories}
        onSelect={addFilter}
        iconOnly={hasAnyChipFilter}
      />
      {hasAnyChipFilter && (
        <Button
          variant="ghost"
          size="sm"
          className="text-sm text-muted-foreground hover:text-foreground"
          onClick={resetAllFilters}
        >
          Reset filters
        </Button>
      )}
    </div>
  );
}
